import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  styled,
  alpha,
} from '@mui/material';

const MailTo = styled('a')(({ theme }) => ({
  color: theme.palette.primary.main,
  textDecorationColor: alpha(theme.palette.primary.main, 0.4),

  ':hover': {
    textDecorationColor: theme.palette.primary.main,
  },
}));

const Terms = () => {
  return (
    <Container maxWidth="md">
      <Paper elevation={0} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Website Terms of Use
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Version 1.0
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom>
          Last revised on: May 15, 2025
        </Typography>

        <Box sx={{ my: 4 }}>
          <Typography paragraph>
            The website located at{' '}
            <MailTo href="https://kip.audio">https://kip.audio</MailTo> (the
            &ldquo;Site&rdquo;) is a copyrighted work belonging to KIP Audio,
            LLC (&ldquo;Company&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;, and
            &ldquo;we&rdquo;). Certain features of the Site may be subject to
            additional guidelines, terms, or rules, which will be posted on the
            Site in connection with such features. All such additional terms,
            guidelines, and rules are incorporated by reference into these
            Terms.
          </Typography>

          <Typography paragraph sx={{ fontWeight: 'bold' }}>
            THESE TERMS OF USE (THESE &ldquo;TERMS&rdquo;) SET FORTH THE LEGALLY
            BINDING TERMS AND CONDITIONS THAT GOVERN YOUR USE OF THE SITE. BY
            ACCESSING OR USING THE SITE, YOU ARE ACCEPTING THESE TERMS (ON
            BEHALF OF YOURSELF OR THE ENTITY THAT YOU REPRESENT), AND YOU
            REPRESENT AND WARRANT THAT YOU HAVE THE RIGHT, AUTHORITY, AND
            CAPACITY TO ENTER INTO THESE TERMS (ON BEHALF OF YOURSELF OR THE
            ENTITY THAT YOU REPRESENT). YOU MAY NOT ACCESS OR USE THE SITE OR
            ACCEPT THE TERMS IF YOU ARE NOT AT LEAST 18 YEARS OLD. IF YOU DO NOT
            AGREE WITH ALL OF THE PROVISIONS OF THESE TERMS, DO NOT ACCESS
            AND/OR USE THE SITE.
          </Typography>

          <Typography paragraph sx={{ fontWeight: 'bold' }}>
            PLEASE BE AWARE THAT SECTION 8.2 CONTAINS PROVISIONS GOVERNING HOW
            TO RESOLVE DISPUTES BETWEEN YOU AND COMPANY. AMONG OTHER THINGS,
            SECTION 8.2 INCLUDES AN AGREEMENT TO ARBITRATE WHICH REQUIRES, WITH
            LIMITED EXCEPTIONS, THAT ALL DISPUTES BETWEEN YOU AND US SHALL BE
            RESOLVED BY BINDING AND FINAL ARBITRATION. SECTION 8.2 ALSO CONTAINS
            A CLASS ACTION AND JURY TRIAL WAIVER. PLEASE READ SECTION 8.2
            CAREFULLY.
          </Typography>

          <Typography paragraph sx={{ fontWeight: 'bold' }}>
            UNLESS YOU OPT OUT OF THE AGREEMENT TO ARBITRATE WITHIN 30 DAYS: (1)
            YOU WILL ONLY BE PERMITTED TO PURSUE DISPUTES OR CLAIMS AND SEEK
            RELIEF AGAINST US ON AN INDIVIDUAL BASIS, NOT AS A PLAINTIFF OR
            CLASS MEMBER IN ANY CLASS OR REPRESENTATIVE ACTION OR PROCEEDING AND
            YOU WAIVE YOUR RIGHT TO PARTICIPATE IN A CLASS ACTION LAWSUIT OR
            CLASS-WIDE ARBITRATION; AND (2) YOU ARE WAIVING YOUR RIGHT TO PURSUE
            DISPUTES OR CLAIMS AND SEEK RELIEF IN A COURT OF LAW AND TO HAVE A
            JURY TRIAL.
          </Typography>

          <Typography variant="h5" component="h2" sx={{ mt: 4, mb: 2 }}>
            1. Accounts
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            1.1 Account Creation
          </Typography>
          <Typography paragraph>
            In order to use certain features of the Site, you must register for
            an account (&ldquo;Account&rdquo;) and provide certain information
            about yourself as prompted by the account registration form. You
            represent and warrant that: (a) all required registration
            information you submit is truthful and accurate; (b) you will
            maintain the accuracy of such information. You may delete your
            Account at any time, for any reason, by following the instructions
            on the Site. Company may suspend or terminate your Account in
            accordance with Section 7.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            1.2 Account Responsibilities
          </Typography>
          <Typography paragraph>
            You are responsible for maintaining the confidentiality of your
            Account login information and are fully responsible for all
            activities that occur under your Account. You agree to immediately
            notify Company of any unauthorized use, or suspected unauthorized
            use of your Account or any other breach of security. Company cannot
            and will not be liable for any loss or damage arising from your
            failure to comply with the above requirements.
          </Typography>

          <Typography variant="h5" component="h2" sx={{ mt: 4, mb: 2 }}>
            2. Access to the Site
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            2.1 License
          </Typography>
          <Typography paragraph>
            Subject to these Terms, Company grants you a non-transferable,
            non-exclusive, revocable, limited license to use and access the Site
            solely for your own personal, noncommercial use.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            2.2 Certain Restrictions
          </Typography>
          <Typography paragraph>
            The rights granted to you in these Terms are subject to the
            following restrictions: (a) you shall not license, sell, rent,
            lease, transfer, assign, distribute, host, or otherwise commercially
            exploit the Site, whether in whole or in part, or any content
            displayed on the Site; (b) you shall not modify, make derivative
            works of, disassemble, reverse compile or reverse engineer any part
            of the Site; (c) you shall not access the Site in order to build a
            similar or competitive website, product, or service; and (d) except
            as expressly stated herein, no part of the Site may be copied,
            reproduced, distributed, republished, downloaded, displayed, posted
            or transmitted in any form or by any means. Unless otherwise
            indicated, any future release, update, or other addition to
            functionality of the Site shall be subject to these Terms. All
            copyright and other proprietary notices on the Site (or on any
            content displayed on the Site) must be retained on all copies
            thereof.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            2.3 Modification
          </Typography>
          <Typography paragraph>
            Company reserves the right, at any time, to modify, suspend, or
            discontinue the Site (in whole or in part) with or without notice to
            you. You agree that Company will not be liable to you or to any
            third party for any modification, suspension, or discontinuation of
            the Site or any part thereof.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            2.4 No Support or Maintenance
          </Typography>
          <Typography paragraph>
            You acknowledge and agree that Company will have no obligation to
            provide you with any support or maintenance in connection with the
            Site.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            2.5 Ownership
          </Typography>
          <Typography paragraph>
            You acknowledge that all the intellectual property rights, including
            copyrights, patents, trade marks, and trade secrets, in the Site and
            its content are owned by Company or Company&rsquo;s suppliers.
            Neither these Terms (nor your access to the Site) transfers to you
            or any third party any rights, title or interest in or to such
            intellectual property rights, except for the limited access rights
            expressly set forth in Section 2.1. Company and its suppliers
            reserve all rights not granted in these Terms. There are no implied
            licenses granted under these Terms.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            2.6 Feedback
          </Typography>
          <Typography paragraph>
            If you provide Company with any feedback or suggestions regarding
            the Site (&ldquo;Feedback&rdquo;), you hereby assign to Company all
            rights in such Feedback and agree that Company shall have the right
            to use and fully exploit such Feedback and related information in
            any manner it deems appropriate. Company will treat any Feedback you
            provide to Company as non-confidential and non-proprietary. You
            agree that you will not submit to Company any information or ideas
            that you consider to be confidential or proprietary.
          </Typography>

          <Typography variant="h5" component="h2" sx={{ mt: 4, mb: 2 }}>
            3. Indemnification
          </Typography>
          <Typography paragraph>
            You agree to indemnify and hold Company (and its officers,
            employees, and agents) harmless, including costs and
            attorneys&rsquo; fees, from any claim or demand made by any third
            party due to or arising out of (a) your use of the Site, (b) your
            violation of these Terms or (c) your violation of applicable laws or
            regulations. Company reserves the right, at your expense, to assume
            the exclusive defense and control of any matter for which you are
            required to indemnify us, and you agree to cooperate with our
            defense of these claims. You agree not to settle any matter without
            the prior written consent of Company. Company will use reasonable
            efforts to notify you of any such claim, action or proceeding upon
            becoming aware of it.
          </Typography>

          <Typography variant="h5" component="h2" sx={{ mt: 4, mb: 2 }}>
            4. Third-Party MailTos & Ads; Other Users
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            4.1 Third-Party MailTos & Ads
          </Typography>
          <Typography paragraph>
            The Site may contain mailtos to third-party websites and services,
            and/or display advertisements for third parties (collectively,
            &ldquo;Third-Party MailTos & Ads&rdquo;). Such Third-Party MailTos &
            Ads are not under the control of Company, and Company is not
            responsible for any Third-Party MailTos & Ads. Company provides
            access to these Third-Party MailTos & Ads only as a convenience to
            you, and does not review, approve, monitor, endorse, warrant, or
            make any representations with respect to Third-Party MailTos & Ads.
            You use all Third-Party MailTos & Ads at your own risk, and should
            apply a suitable level of caution and discretion in doing so. When
            you click on any of the Third-Party MailTos & Ads, the applicable
            third party&rsquo;s terms and policies apply, including the third
            party&rsquo;s privacy and data gathering practices. You should make
            whatever investigation you feel necessary or appropriate before
            proceeding with any transaction in connection with such Third-Party
            MailTos & Ads.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            4.2 Other Users
          </Typography>
          <Typography paragraph>
            Your interactions with other Site users are solely between you and
            such users. You agree that Company will not be responsible for any
            loss or damage incurred as the result of any such interactions. If
            there is a dispute between you and any Site user, we are under no
            obligation to become involved.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            4.3 Release
          </Typography>
          <Typography paragraph>
            You hereby release and forever discharge Company (and our officers,
            employees, agents, successors, and assigns) from, and hereby waive
            and relinquish, each and every past, present and future dispute,
            claim, controversy, demand, right, obligation, liability, action and
            cause of action of every kind and nature (including personal
            injuries, death, and property damage), that has arisen or arises
            directly or indirectly out of, or that relates directly or
            indirectly to, the Site (including any interactions with, or act or
            omission of, other Site users or any Third-Party MailTos & Ads). IF
            YOU ARE A CALIFORNIA RESIDENT, YOU HEREBY WAIVE CALIFORNIA CIVIL
            CODE SECTION 1542 IN CONNECTION WITH THE FOREGOING, WHICH STATES:
            &ldquo;A GENERAL RELEASE DOES NOT EXTEND TO CLAIMS WHICH THE
            CREDITOR OR RELEASING PARTY DOES NOT KNOW OR SUSPECT TO EXIST IN HIS
            OR HER FAVOR AT THE TIME OF EXECUTING THE RELEASE, WHICH IF KNOWN BY
            HIM OR HER MUST HAVE MATERIALLY AFFECTED HIS OR HER SETTLEMENT WITH
            THE DEBTOR OR RELEASED PARTY.&rdquo;
          </Typography>

          <Typography variant="h5" component="h2" sx={{ mt: 4, mb: 2 }}>
            5. Disclaimers
          </Typography>
          <Typography paragraph sx={{ fontWeight: 'bold' }}>
            THE SITE IS PROVIDED ON AN &ldquo;AS-IS&rdquo; AND &ldquo;AS
            AVAILABLE&rdquo; BASIS, AND COMPANY (AND OUR SUPPLIERS) EXPRESSLY
            DISCLAIM ANY AND ALL WARRANTIES AND CONDITIONS OF ANY KIND, WHETHER
            EXPRESS, IMPLIED, OR STATUTORY, INCLUDING ALL WARRANTIES OR
            CONDITIONS OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            TITLE, QUIET ENJOYMENT, ACCURACY, OR NON-INFRINGEMENT. WE (AND OUR
            SUPPLIERS) MAKE NO WARRANTY THAT THE SITE WILL MEET YOUR
            REQUIREMENTS, WILL BE AVAILABLE ON AN UNINTERRUPTED, TIMELY, SECURE,
            OR ERROR-FREE BASIS, OR WILL BE ACCURATE, RELIABLE, FREE OF VIRUSES
            OR OTHER HARMFUL CODE, COMPLETE, LEGAL, OR SAFE. IF APPLICABLE LAW
            REQUIRES ANY WARRANTIES WITH RESPECT TO THE SITE, ALL SUCH
            WARRANTIES ARE LIMITED IN DURATION TO 90 DAYS FROM THE DATE OF FIRST
            USE.
          </Typography>
          <Typography paragraph>
            SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF IMPLIED WARRANTIES,
            SO THE ABOVE EXCLUSION MAY NOT APPLY TO YOU. SOME JURISDICTIONS DO
            NOT ALLOW LIMITATIONS ON HOW LONG AN IMPLIED WARRANTY LASTS, SO THE
            ABOVE LIMITATION MAY NOT APPLY TO YOU.
          </Typography>

          <Typography variant="h5" component="h2" sx={{ mt: 4, mb: 2 }}>
            6. Limitation on Liability
          </Typography>
          <Typography paragraph sx={{ fontWeight: 'bold' }}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL COMPANY
            (OR OUR SUPPLIERS) BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY LOST
            PROFITS, LOST DATA, COSTS OF PROCUREMENT OF SUBSTITUTE PRODUCTS, OR
            ANY INDIRECT, CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL OR
            PUNITIVE DAMAGES ARISING FROM OR RELATING TO THESE TERMS OR YOUR USE
            OF, OR INABILITY TO USE, THE SITE, EVEN IF COMPANY HAS BEEN ADVISED
            OF THE POSSIBILITY OF SUCH DAMAGES. ACCESS TO, AND USE OF, THE SITE
            IS AT YOUR OWN DISCRETION AND RISK, AND YOU WILL BE SOLELY
            RESPONSIBLE FOR ANY DAMAGE TO YOUR DEVICE OR COMPUTER SYSTEM, OR
            LOSS OF DATA RESULTING THEREFROM.
          </Typography>
          <Typography paragraph sx={{ fontWeight: 'bold' }}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, NOTWITHSTANDING ANYTHING TO
            THE CONTRARY CONTAINED HEREIN, OUR LIABILITY TO YOU FOR ANY DAMAGES
            ARISING FROM OR RELATED TO THESE TERMS (FOR ANY CAUSE WHATSOEVER AND
            REGARDLESS OF THE FORM OF THE ACTION), WILL AT ALL TIMES BE LIMITED
            TO A MAXIMUM OF FIFTY US DOLLARS. THE EXISTENCE OF MORE THAN ONE
            CLAIM WILL NOT ENLARGE THIS LIMIT. YOU AGREE THAT OUR SUPPLIERS WILL
            HAVE NO LIABILITY OF ANY KIND ARISING FROM OR RELATING TO THESE
            TERMS.
          </Typography>
          <Typography paragraph>
            SOME JURISDICTIONS DO NOT ALLOW THE LIMITATION OR EXCLUSION OF
            LIABILITY FOR INCIDENTAL OR CONSEQUENTIAL DAMAGES, SO THE ABOVE
            LIMITATION OR EXCLUSION MAY NOT APPLY TO YOU.
          </Typography>

          <Typography variant="h5" component="h2" sx={{ mt: 4, mb: 2 }}>
            7. Term and Termination
          </Typography>
          <Typography paragraph>
            Subject to this Section, these Terms will remain in full force and
            effect while you use the Site. We may suspend or terminate your
            rights to use the Site (including your Account) at any time for any
            reason at our sole discretion, including for any use of the Site in
            violation of these Terms. Upon termination of your rights under
            these Terms, your Account and right to access and use the Site will
            terminate immediately. Company will not have any liability
            whatsoever to you for any termination of your rights under these
            Terms, including for termination of your Account. Even after your
            rights under these Terms are terminated, the following provisions of
            these Terms will remain in effect: Sections 2.2 through 2.6 and
            Sections 3 through 8.
          </Typography>

          <Typography variant="h5" component="h2" sx={{ mt: 4, mb: 2 }}>
            8. General
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            8.1 Changes
          </Typography>
          <Typography paragraph>
            These Terms are subject to occasional revision, and if we make any
            substantial changes, we may notify you by sending you an e-mail to
            the last e-mail address you provided to us (if any), and/or by
            prominently posting notice of the changes on our Site. You are
            responsible for providing us with your most current e-mail address.
            In the event that the last e-mail address that you have provided us
            is not valid, or for any reason is not capable of delivering to you
            the notice described above, our dispatch of the e-mail containing
            such notice will nonetheless constitute effective notice of the
            changes described in the notice. Continued use of our Site following
            notice of such changes shall indicate your acknowledgement of such
            changes and agreement to be bound by the terms and conditions of
            such changes.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            8.2 Dispute Resolution
          </Typography>
          <Typography paragraph>
            Please read the following arbitration agreement in this Section (the
            &ldquo;Arbitration Agreement&rdquo;) carefully. It requires you to
            arbitrate disputes with Company, its parent companies, subsidiaries,
            affiliates, successors and assigns and all of their respective
            officers, directors, employees, agents, and representatives
            (collectively, the &ldquo;Company Parties&rdquo;) and limits the
            manner in which you can seek relief from the Company Parties.
          </Typography>

          <Typography variant="subtitle1" gutterBottom>
            Applicability of Arbitration Agreement
          </Typography>
          <Typography paragraph>
            You agree that any dispute between you and any of the Company
            Parties relating in any way to the Site, the services offered on the
            Site (the &ldquo;Services&rdquo;) or these Terms will be resolved by
            binding arbitration, rather than in court, except that (1) you and
            the Company Parties may assert individualized claims in small claims
            court if the claims qualify, remain in such court and advance solely
            on an individual, non-class basis; and (2) you or the Company
            Parties may seek equitable relief in court for infringement or other
            misuse of intellectual property rights (such as trademarks, trade
            dress, domain names, trade secrets, copyrights, and patents). This
            Arbitration Agreement shall survive the expiration or termination of
            these Terms and shall apply, without limitation, to all claims that
            arose or were asserted before you agreed to these Terms (in
            accordance with the preamble) or any prior version of these Terms.
            This Arbitration Agreement does not preclude you from bringing
            issues to the attention of federal, state or local agencies. Such
            agencies can, if the law allows, seek relief against the Company
            Parties on your behalf. For purposes of this Arbitration Agreement,
            &ldquo;Dispute&rdquo; will also include disputes that arose or
            involve facts occurring before the existence of this or any prior
            versions of the Agreement as well as claims that may arise after the
            termination of these Terms.
          </Typography>

          <Typography variant="subtitle1" gutterBottom>
            Informal Dispute Resolution
          </Typography>
          <Typography paragraph>
            There might be instances when a Dispute arises between you and
            Company. If that occurs, Company is committed to working with you to
            reach a reasonable resolution. You and Company agree that good faith
            informal efforts to resolve Disputes can result in a prompt,
            low‐cost and mutually beneficial outcome. You and Company therefore
            agree that before either party commences arbitration against the
            other (or initiates an action in small claims court if a party so
            elects), we will personally meet and confer telephonically or via
            videoconference, in a good faith effort to resolve informally any
            Dispute covered by this Arbitration Agreement (&ldquo;Informal
            Dispute Resolution Conference&rdquo;). If you are represented by
            counsel, your counsel may participate in the conference, but you
            will also participate in the conference.
          </Typography>
          <Typography paragraph>
            The party initiating a Dispute must give notice to the other party
            in writing of its intent to initiate an Informal Dispute Resolution
            Conference (&ldquo;Notice&rdquo;), which shall occur within 45 days
            after the other party receives such Notice, unless an extension is
            mutually agreed upon by the parties. Notice to Company that you
            intend to initiate an Informal Dispute Resolution Conference should
            be sent by email to:{' '}
            <MailTo href="mailto:pax@kip.audio">pax@kip.audio</MailTo>, or by
            regular mail to 1308 N West St, Wilmington, Delaware 19801. The
            Notice must include: (1) your name, telephone number, mailing
            address, e‐mail address associated with your account (if you have
            one); (2) the name, telephone number, mailing address and e‐mail
            address of your counsel, if any; and (3) a description of your
            Dispute.
          </Typography>
          <Typography paragraph>
            The Informal Dispute Resolution Conference shall be individualized
            such that a separate conference must be held each time either party
            initiates a Dispute, even if the same law firm or group of law firms
            represents multiple users in similar cases, unless all parties
            agree; multiple individuals initiating a Dispute cannot participate
            in the same Informal Dispute Resolution Conference unless all
            parties agree. In the time between a party receiving the Notice and
            the Informal Dispute Resolution Conference, nothing in this
            Arbitration Agreement shall prohibit the parties from engaging in
            informal communications to resolve the initiating party&rsquo;s
            Dispute. Engaging in the Informal Dispute Resolution Conference is a
            condition precedent and requirement that must be fulfilled before
            commencing arbitration. The statute of limitations and any filing
            fee deadlines shall be tolled while the parties engage in the
            Informal Dispute Resolution Conference process required by this
            section.
          </Typography>

          <Typography variant="subtitle1" gutterBottom>
            Arbitration Rules and Forum
          </Typography>
          <Typography paragraph>
            These Terms evidence a transaction involving interstate commerce;
            and notwithstanding any other provision herein with respect to the
            applicable substantive law, the Federal Arbitration Act, 9 U.S.C. §
            1 et seq., will govern the interpretation and enforcement of this
            Arbitration Agreement and any arbitration proceedings. If the
            Informal Dispute Resolution Process described above does not resolve
            satisfactorily within 60 days after receipt of your Notice, you and
            Company agree that either party shall have the right to finally
            resolve the Dispute through binding arbitration. The Federal
            Arbitration Act governs the interpretation and enforcement of this
            Arbitration Agreement. The arbitration will be conducted by JAMS, an
            established alternative dispute resolution provider. Disputes
            involving claims and counterclaims with an amount in controversy
            under $250,000, not inclusive of attorneys&rsquo; fees and interest,
            shall be subject to JAMS&rsquo; most current version of the
            Streamlined Arbitration Rules and procedures available at{' '}
            <MailTo
              href="http://www.jamsadr.com/rules-streamlined-arbitration/"
              target="_blank"
              rel="noopener noreferrer"
            >
              http://www.jamsadr.com/rules-streamlined-arbitration/
            </MailTo>
            ; all other claims shall be subject to JAMS&rsquo;s most current
            version of the Comprehensive Arbitration Rules and Procedures,
            available at{' '}
            <MailTo
              href="http://www.jamsadr.com/rules-comprehensive-arbitration/"
              target="_blank"
              rel="noopener noreferrer"
            >
              http://www.jamsadr.com/rules-comprehensive-arbitration/
            </MailTo>
            . JAMS&rsquo;s rules are also available at{' '}
            <MailTo
              href="http://www.jamsadr.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.jamsadr.com
            </MailTo>{' '}
            or by calling JAMS at 800-352-5267.
          </Typography>

          <Typography variant="subtitle1" gutterBottom>
            Authority of Arbitrator
          </Typography>
          <Typography paragraph>
            The arbitrator shall have exclusive authority to resolve all
            disputes subject to arbitration hereunder including, without
            limitation, any dispute related to the interpretation,
            applicability, enforceability or formation of this Arbitration
            Agreement or any portion of the Arbitration Agreement, except for
            the following: (1) all Disputes arising out of or relating to the
            subsection entitled &ldquo;Waiver of Class or Other
            Non-Individualized Relief,&rdquo; including any claim that all or
            part of the subsection entitled &ldquo;Waiver of Class or Other
            Non-Individualized Relief&rdquo; is unenforceable, illegal, void or
            voidable, or that such subsection entitled &ldquo;Waiver of Class or
            Other Non-Individualized Relief&rdquo; has been breached, shall be
            decided by a court of competent jurisdiction and not by an
            arbitrator; (2) except as expressly contemplated in the subsection
            entitled &ldquo;Batch Arbitration,&rdquo; all Disputes about the
            payment of arbitration fees shall be decided only by a court of
            competent jurisdiction and not by an arbitrator; (3) all Disputes
            about whether either party has satisfied any condition precedent to
            arbitration shall be decided only by a court of competent
            jurisdiction and not by an arbitrator; and (4) all Disputes about
            which version of the Arbitration Agreement applies shall be decided
            only by a court of competent jurisdiction and not by an arbitrator.
          </Typography>

          <Typography variant="subtitle1" gutterBottom>
            Waiver of Jury Trial
          </Typography>
          <Typography paragraph sx={{ fontWeight: 'bold' }}>
            EXCEPT AS SPECIFIED IN SECTION 8.2(A) YOU AND THE COMPANY PARTIES
            HEREBY WAIVE ANY CONSTITUTIONAL AND STATUTORY RIGHTS TO SUE IN COURT
            AND HAVE A TRIAL IN FRONT OF A JUDGE OR A JURY. You and the Company
            Parties are instead electing that all covered claims and disputes
            shall be resolved exclusively by arbitration under this Arbitration
            Agreement, except as specified in Section 8.2(a) above. An
            arbitrator can award on an individual basis the same damages and
            relief as a court and must follow these Terms as a court would.
            However, there is no judge or jury in arbitration, and court review
            of an arbitration award is subject to very limited review.
          </Typography>

          <Typography variant="subtitle1" gutterBottom>
            Waiver of Class or Other Non-Individualized Relief
          </Typography>
          <Typography paragraph sx={{ fontWeight: 'bold' }}>
            YOU AND COMPANY AGREE THAT, EXCEPT AS SPECIFIED IN SUBSECTION 8.2(H)
            EACH OF US MAY BRING CLAIMS AGAINST THE OTHER ONLY ON AN INDIVIDUAL
            BASIS AND NOT ON A CLASS, REPRESENTATIVE, OR COLLECTIVE BASIS, AND
            THE PARTIES HEREBY WAIVE ALL RIGHTS TO HAVE ANY DISPUTE BE BROUGHT,
            HEARD, ADMINISTERED, RESOLVED, OR ARBITRATED ON A CLASS, COLLECTIVE,
            REPRESENTATIVE, OR MASS ACTION BASIS. ONLY INDIVIDUAL RELIEF IS
            AVAILABLE, AND DISPUTES OF MORE THAN ONE CUSTOMER OR USER CANNOT BE
            ARBITRATED OR CONSOLIDATED WITH THOSE OF ANY OTHER CUSTOMER OR USER.
          </Typography>

          <Typography variant="subtitle1" gutterBottom>
            30-Day Right to Opt Out
          </Typography>
          <Typography paragraph>
            You have the right to opt out of the provisions of this Arbitration
            Agreement by sending a timely written notice of your decision to opt
            out to the following address: 1308 N West St, Wilmington, Delaware
            19801, or email to{' '}
            <MailTo href="mailto:pax@kip.audio">pax@kip.audio</MailTo>, within
            30 days after first becoming subject to this Arbitration Agreement.
            Your notice must include your name and address and a clear statement
            that you want to opt out of this Arbitration Agreement. If you opt
            out of this Arbitration Agreement, all other parts of these Terms
            will continue to apply to you. Opting out of this Arbitration
            Agreement has no effect on any other arbitration agreements that you
            may currently have with us, or may enter into in the future with us.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            8.3 Export
          </Typography>
          <Typography paragraph>
            The Site may be subject to U.S. export control laws and may be
            subject to export or import regulations in other countries. You
            agree not to export, reexport, or transfer, directly or indirectly,
            any U.S. technical data acquired from Company, or any products
            utilizing such data, in violation of the United States export laws
            or regulations.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            8.4 Disclosures
          </Typography>
          <Typography paragraph>
            Company is located at the address in Section 8.8. If you are a
            California resident, you may report complaints to the Complaint
            Assistance Unit of the Division of Consumer Product of the
            California Department of Consumer Affairs by contacting them in
            writing at 400 R Street, Sacramento, CA 95814, or by telephone at
            (800) 952-5210.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            8.5 Electronic Communications
          </Typography>
          <Typography paragraph>
            The communications between you and Company use electronic means,
            whether you use the Site or send us emails, or whether Company posts
            notices on the Site or communicates with you via email. For
            contractual purposes, you (a) consent to receive communications from
            Company in an electronic form; and (b) agree that all terms and
            conditions, agreements, notices, disclosures, and other
            communications that Company provides to you electronically satisfy
            any legal requirement that such communications would satisfy if it
            were be in a hardcopy writing. The foregoing does not affect your
            non-waivable rights.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            8.6 Entire Terms
          </Typography>
          <Typography paragraph>
            These Terms constitute the entire agreement between you and us
            regarding the use of the Site. Our failure to exercise or enforce
            any right or provision of these Terms shall not operate as a waiver
            of such right or provision. The section titles in these Terms are
            for convenience only and have no legal or contractual effect. The
            word &ldquo;including&rdquo; means &ldquo;including without
            limitation&rdquo;. If any provision of these Terms is, for any
            reason, held to be invalid or unenforceable, the other provisions of
            these Terms will be unimpaired and the invalid or unenforceable
            provision will be deemed modified so that it is valid and
            enforceable to the maximum extent permitted by law. Your
            relationship to Company is that of an independent contractor, and
            neither party is an agent or partner of the other. These Terms, and
            your rights and obligations herein, may not be assigned,
            subcontracted, delegated, or otherwise transferred by you without
            Company&rsquo;s prior written consent, and any attempted assignment,
            subcontract, delegation, or transfer in violation of the foregoing
            will be null and void. Company may freely assign these Terms. The
            terms and conditions set forth in these Terms shall be binding upon
            assignees.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            8.7 Copyright/Trademark Information
          </Typography>
          <Typography paragraph>
            Copyright © 2025 KIP Audio, LLC. All rights reserved. All
            trademarks, logos and service marks (&ldquo;Marks&rdquo;) displayed
            on the Site are our property or the property of other third parties.
            You are not permitted to use these Marks without our prior written
            consent or the consent of such third party which may own the Marks.
          </Typography>

          <Typography variant="h6" component="h3" gutterBottom>
            8.8 Contact Information
          </Typography>
          <Typography paragraph>
            KIP Audio, LLC
            <br />
            PO BOX 323
            <br />
            Wilmington, Delaware 19899
            <br />
            Email: <MailTo href="mailto:kip@kip.audio">kip@kip.audio</MailTo>
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default Terms;
